import type { Domain } from "~/api/Api";

import { FormData } from "../types";

export type IntegrationType = Domain["integration_type"];

export type Ctx = {
  getType: () => string;
  integrationType: IntegrationType;
  isDuplicate: () => boolean;
};

export type ValidatorFn<V> = (value: V, ctx: Ctx) => true | string;

type ValidatorMap<F> = { [K in keyof F]?: ValidatorFn<F[K]> };

export function duplicateKey(
  type: string,
  nameFQDN: string,
  content: string,
): string {
  const n = String(nameFQDN || "").toLowerCase();
  const c = String(content || "")
    .trim()
    .replace(/\s+/g, " ");

  return `${type}|${n}|${c}`;
}

export const validators: ValidatorMap<FormData> = {
  type(value: string) {
    if (!value) return "Type is required";

    return true;
  },

  ttl(value: string | number) {
    const n = Number(value);
    if (value === "" || value == null) return "TTL is required";
    if (Number.isNaN(n)) return "TTL must be a number";
    if (n < 0) return "Min value is 0";
    if (n > 2147483647) return "Max value is 2147483647";

    return true;
  },

  content(value: string | number, ctx: Ctx) {
    const v = String(value ?? "").trim();
    const dnsType = ctx.getType();

    if (!v) return "Value is required";

    if (ctx.integrationType === "partial" && dnsType === "CNAME" && v === "@") {
      return "Please specify the hostname explicitly.";
    }

    if (ctx.isDuplicate()) {
      return "A record with the same Type, Name and Value already exists.";
    }

    return true;
  },

  priority(value: string | number) {
    const n = Number(value);
    if (value === "" || value == null) return "Priority is required";
    if (Number.isNaN(n)) return "Priority must be a number";
    if (n < 0) return "Min value is 0";
    if (n > 65535) return "Max value is 65535";

    return true;
  },

  weight(value: string | number) {
    const n = Number(value);
    if (value === "" || value == null) return "Weight is required";
    if (Number.isNaN(n)) return "Weight must be a number";
    if (n < 0) return "Min value is 0";

    return true;
  },

  port(value: string | number) {
    const n = Number(value);
    if (value === "" || value == null) return "Port is required";
    if (Number.isNaN(n)) return "Port must be a number";
    if (n < 0) return "Min value is 0";

    return true;
  },

  flags(value: string | number) {
    const n = Number(value);
    if (value === "" || value == null) return "Flags is required";
    if (Number.isNaN(n)) return "Flags must be a number";
    if (n < 0) return "Min value is 0";
    if (n > 255) return "Max value is 255";

    return true;
  },

  tag(value: string) {
    if (!value) return "Tag is required";

    return true;
  },

  subdomain(value: string | number) {
    const v = String(value ?? "").trim();
    if (v === "" || v === "@") return true;
    if (v.endsWith(".")) return "Name (subdomain) must not end with a dot.";

    return true;
  },

  baseDomain() {
    return true;
  },

  name() {
    return true;
  },
};
